import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Box, Button, SkeletTable } from '@alf/uikit';
import { LOG_ACTIONS, useLogAmplitude } from '@namespace/analytics';
import { useGeneralRoutes } from '@namespace/cms';
import { BonusHunterWrapper, SelfExcludedWrapper } from '@namespace/common';
import { useGetInHouseJackpotSettings } from '@namespace/games';
import { scrollTo, useTimeout } from '@namespace/helpers';
import { DATE_FORMATS_TYPES, LocalizedLink, useI18n } from '@namespace/i18n';
import { UserContext } from '@namespace/user';
import { PersonalOfficeWrapper } from '../../../../account';
import { BonusCardByType } from '../../common/BonusCardByType';
import EmptyBonusList from '../../common/EmptyBonusList';
import {
  ACTIVATION_CHECK_DELAY,
  freeSpinStates,
  fundsStates,
  MAX_ACTIVATION_CHECK_ATTEMPT,
  OTHER_BONUS_TYPES,
  riskFreeStates
} from '../../constants';
import { useAllTypesBonusesFetchCallbacks } from '../../hooks/useAllTypesBonusesFetchCallbacks';
import useGetBonusesCounters from '../../hooks/useGetBonusesCounters';
import { useBonusesStore } from '../../store';

import styles from './index.module.css';

const AllTypesBonusesListCommon = ({ className, CustomEmptyState }) => {
  const [reloadCount, setReloadCount] = useState(1);
  const { t, f } = useI18n();
  const [user, userActions] = useContext(UserContext);
  const { FETCH_USER_DATA } = userActions;
  const { registrationDate } = user;
  const logAmplitude = useLogAmplitude();

  const allTypesBonuses = useBonusesStore('allTypesBonuses');
  const {
    ids: bonusesIds,
    isNextPageExist,
    data: bonusesList
  } = allTypesBonuses;
  const {
    loadBonusesList,
    loadMoreBonusesList,
    isFetchingBonuses,
    isFetchingMoreBonuses
  } = useAllTypesBonusesFetchCallbacks();

  const { isJackpotBonusTabEnabled } = useGetInHouseJackpotSettings();

  const { allTypesBonusesHistory } = useGeneralRoutes();
  const fetchCounters = useGetBonusesCounters([
    ...(isJackpotBonusTabEnabled ? [OTHER_BONUS_TYPES.JACKPOT_WINS] : [])
  ]);

  const isActivation = useMemo(
    () =>
      Object.values(bonusesList).some(
        ({ state }) =>
          state === fundsStates.PROPOSED_ACCEPT_REQUEST_BY_GAMER ||
          state === riskFreeStates.ERF_STATE_ACTIVATION_REQUEST ||
          state === freeSpinStates.FREESPIN_ACCEPT_REQUEST
      ),
    [bonusesList]
  );

  const onCheckActivation = useCallback(() => {
    setReloadCount((prev) => prev + 1);
    loadBonusesList();
  }, [loadBonusesList]);

  const onAfterAccept = useCallback(() => {
    fetchCounters();
    loadBonusesList();
    FETCH_USER_DATA();
    scrollTo({ behavior: 'smooth' });
  }, [FETCH_USER_DATA, fetchCounters, loadBonusesList]);

  useEffect(() => {
    logAmplitude({
      actionName: LOG_ACTIONS.BONUSES_OPEN,
      attributes: {
        bonus_tab: 'bonuses',
        registration_date: f
          .getDateTime({ date: registrationDate?.replace('Z', '') }) // time on UTC-0
          .toFormat(DATE_FORMATS_TYPES.API_WITH_TIME)
      }
    });
    loadBonusesList();
  }, []);

  useTimeout(
    onCheckActivation,
    !isActivation || reloadCount > MAX_ACTIVATION_CHECK_ATTEMPT
      ? null
      : ACTIVATION_CHECK_DELAY * reloadCount
  );

  if (isFetchingBonuses) {
    return (
      <SkeletTable
        classNames={{
          classNameItem: styles.skeletHeight,
          classNameWrapper: clsx([styles.skeletWrapper, className])
        }}
      />
    );
  }

  return (
    <PersonalOfficeWrapper wrapperClassName={className}>
      {!bonusesIds.length &&
        (CustomEmptyState ? (
          <CustomEmptyState />
        ) : (
          <EmptyBonusList
            title={t('web.personaloffice.bonuses.title.emptylist')}
          />
        ))}
      {bonusesIds.length > 0 && (
        <>
          {bonusesIds.map((bonusId) => (
            <BonusCardByType
              key={bonusId}
              bonusId={bonusId}
              onAccept={onAfterAccept}
              onWagerAccept={onAfterAccept}
            />
          ))}
        </>
      )}
      <Box className={styles.buttonsContainer}>
        {isNextPageExist && (
          <Button
            size="m"
            actionType="color3"
            intent="quaternary"
            onClick={loadMoreBonusesList}
            processing={isFetchingMoreBonuses}
          >
            {t('web.personaloffice.bonuses.button.showmore')}
          </Button>
        )}
        <Button
          component={LocalizedLink}
          to={allTypesBonusesHistory}
          size="m"
          actionType="color3"
          intent="secondary"
        >
          {t('web.personaloffice.bonuses.button.bonushistory')}
        </Button>
      </Box>
    </PersonalOfficeWrapper>
  );
};

export const AllTypesBonusesList = ({ className, CustomEmptyState }) => (
  <SelfExcludedWrapper>
    <BonusHunterWrapper>
      <AllTypesBonusesListCommon
        className={className}
        CustomEmptyState={CustomEmptyState}
      />
    </BonusHunterWrapper>
  </SelfExcludedWrapper>
);
